/**
 * Where the Running system section is placed.
 *
 *   TeamRunningSystem        on the Team page, a fold under the queue
 *   RepositoryRunningSystem  in a repository's sheet under Connections, one
 *                            panel per Team whose system builds from it
 *
 * Both render nothing at all (no title, no fold) unless the server answers:
 * a 404 means running copies are off for the workspace (the per-workspace
 * flag the client cannot read) or the Team is not the reader's to see, and a
 * failed read stays quiet for the same reason.
 */
import { useEffect, useState } from "react";
import type { SystemRepositoryTeamsResponse } from "@yagni/shared";
import { Fold } from "../../queue/index.js";
import { SystemPanel } from "./SystemPanel.js";
import { SECTION_TITLE, SYSTEM_STATE_WORD } from "./systemCopy.js";
import { systemApi as defaultApi, type SystemApi } from "./systemApi.js";
import { useTeamSystem } from "./useTeamSystem.js";

export function TeamRunningSystem({
  teamId,
  api = defaultApi,
  now,
}: {
  teamId: string;
  api?: SystemApi;
  now?: number;
}) {
  const system = useTeamSystem(api, teamId);
  // A failed read is quiet too: the client cannot tell a workspace with
  // running copies from one without, and the queue stands on its own.
  if (system.status !== "ready" || !system.data) return null;
  const state = system.data.environment?.state;
  return (
    <Fold
      title={SECTION_TITLE}
      count={state ? SYSTEM_STATE_WORD[state] : undefined}
      className="running-system"
    >
      <SystemPanel
        teamId={teamId}
        data={system.data}
        api={api}
        onChanged={() => void system.reread()}
        inFold
        now={now}
      />
    </Fold>
  );
}

function RepositoryTeamPanel({
  teamId,
  teamName,
  api,
  now,
}: {
  teamId: string;
  teamName: string;
  api: SystemApi;
  now?: number;
}) {
  const system = useTeamSystem(api, teamId);
  if (system.status !== "ready" || !system.data) return null;
  return (
    <SystemPanel
      teamId={teamId}
      teamName={teamName}
      data={system.data}
      api={api}
      onChanged={() => void system.reread()}
      now={now}
    />
  );
}

export function RepositoryRunningSystem({
  repoId,
  api = defaultApi,
  now,
}: {
  repoId: string;
  api?: SystemApi;
  now?: number;
}) {
  const [teams, setTeams] = useState<SystemRepositoryTeamsResponse["teams"] | null>(null);
  useEffect(() => {
    let live = true;
    setTeams(null);
    api
      .readRepositoryTeams(repoId)
      .then((response) => {
        if (live) setTeams(response?.teams ?? null);
      })
      .catch(() => {
        // Quiet: the sheet's own sections stand without this one.
        if (live) setTeams(null);
      });
    return () => {
      live = false;
    };
  }, [api, repoId]);

  if (!teams || teams.length === 0) return null;
  return (
    <div data-testid="repository-running-system">
      {teams.map((team) => (
        <RepositoryTeamPanel
          key={team.teamId}
          teamId={team.teamId}
          teamName={team.name}
          api={api}
          now={now}
        />
      ))}
    </div>
  );
}
